/**
 * Super-admin → Enterprise Controls
 *
 * Single page, 4 tabs (API Keys · White-label · Priority Support · Field Masks)
 * each exposing every configurable parameter + a cross-tenant viewer.
 *
 * Every action is audit-logged on the backend with actor email + target
 * tenant id. Every endpoint requires require_super_admin — tenants cannot
 * reach any of these routes.
 */
import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
    KeyRound, Palette, LifeBuoy, ShieldAlert, BookOpen, Save, Plus, Trash2,
    RefreshCw, Calendar, Building2, Loader2, AlertTriangle,
} from "lucide-react";
import Modal, { GoldButton, GhostButton, PageHeader, Field, Input, Textarea, Select } from "@/components/Ui";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const TABS = [
    { id: "api_keys",         label: "API Keys",         icon: KeyRound },
    { id: "white_label",      label: "White-label",      icon: Palette },
    { id: "priority_support", label: "Priority Support", icon: LifeBuoy },
    { id: "field_masks",      label: "Field Masks",      icon: ShieldAlert },
];

export default function AdminEnterpriseControls() {
    const [tab, setTab] = useState("api_keys");
    const [cfg, setCfg] = useState(null);

    const loadCfg = useCallback(async () => {
        try { const r = await api.get("/admin/enterprise/config"); setCfg(r.data); }
        catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { loadCfg(); }, [loadCfg]);

    if (!cfg) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

    return (
        <>
            <PageHeader title="Enterprise Controls" subtitle="Full configuration of every parameter across API Keys, White-label, Priority Support and Field-level RBAC. All actions audit-logged · tenant-scoped."/>

            <div className="flex gap-1 border-b border-border mb-6 overflow-x-auto">
                {TABS.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                        className={`px-4 py-2 text-xs uppercase tracking-[0.15em] flex items-center gap-2 border-b-2 whitespace-nowrap ${tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                        <t.icon className="w-3.5 h-3.5"/>{t.label}
                    </button>
                ))}
            </div>

            {tab === "api_keys" && <ApiKeysTab cfg={cfg} reload={loadCfg}/>}
            {tab === "white_label" && <WhiteLabelTab cfg={cfg} reload={loadCfg}/>}
            {tab === "priority_support" && <PrioritySupportTab/>}
            {tab === "field_masks" && <FieldMasksTab cfg={cfg} reload={loadCfg}/>}
        </>
    );
}

// ====================================================================
// API KEYS TAB
// ====================================================================
function ApiKeysTab({ cfg, reload }) {
    const [params, setParams] = useState(cfg.api_keys);
    const [scopes, setScopes] = useState([]);
    const [keys, setKeys] = useState([]);
    const [newScope, setNewScope] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const [s, k] = await Promise.all([
                api.get("/admin/enterprise/scope-catalog"),
                api.get("/admin/enterprise/api-keys"),
            ]);
            setScopes(s.data || []); setKeys(k.data || []);
        } catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { setParams(cfg.api_keys); }, [cfg]);

    const saveParams = async () => {
        setSaving(true);
        try { await api.patch("/admin/enterprise/config", { api_keys: params }); toast.success("Saved"); reload(); }
        catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const addScope = async () => {
        try { await api.post("/admin/enterprise/scope-catalog", newScope); toast.success("Scope added"); setNewScope(null); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    const delScope = async (s) => {
        if (!window.confirm(`Delete scope "${s.value}"? Existing keys keep their scopes; only new keys are affected.`)) return;
        try { await api.delete(`/admin/enterprise/scope-catalog/${s.id}`); toast.success("Scope deleted"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    const revokeKey = async (k) => {
        if (!window.confirm(`Force-revoke "${k.name}" for ${k.tenant_name}? Tenant integration will stop immediately.`)) return;
        try { await api.post(`/admin/enterprise/api-keys/${k.id}/revoke`); toast.success("Key revoked"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="space-y-8">
            <ExplainerCard
                title="API Keys — what each parameter does"
                rows={[
                    ["max_keys_per_tenant", "Tenant POST /tenant/api-keys returns 400 once they hit this count of active (non-revoked) keys."],
                    ["key_ttl_days", "If > 0, every issued key gets an expires_at and api_key_auth returns 401 once past."],
                    ["default_scopes", "Pre-selected checkboxes on the tenant's New-key modal. They can change selection — but only to values in the catalog."],
                    ["available_scopes", "Master catalog. Only these strings can be requested in a key. Add custom ones (e.g. cases:write) here first."],
                    ["rate_limit_per_min", "Per-key request budget. (Enforcement TBD in middleware — currently advisory.)"],
                ]}/>

            <div className="bg-card border border-border p-5">
                <div className="overline mb-4">Parameters</div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Max keys per tenant"><Input type="number" min={1} max={100} value={params.max_keys_per_tenant} onChange={e => setParams({...params, max_keys_per_tenant: Number(e.target.value)})} data-testid="max-keys"/></Field>
                    <Field label="Key TTL (days, 0 = never expire)"><Input type="number" min={0} max={3650} value={params.key_ttl_days} onChange={e => setParams({...params, key_ttl_days: Number(e.target.value)})} data-testid="key-ttl"/></Field>
                    <Field label="Rate limit (req/min per key)"><Input type="number" min={1} max={10000} value={params.rate_limit_per_min} onChange={e => setParams({...params, rate_limit_per_min: Number(e.target.value)})} data-testid="rate-limit"/></Field>
                    <Field label="Default scopes (CSV)">
                        <Input value={(params.default_scopes || []).join(", ")}
                            onChange={e => setParams({...params, default_scopes: e.target.value.split(",").map(s=>s.trim()).filter(Boolean)})}
                            data-testid="default-scopes"/>
                    </Field>
                </div>
                <div className="flex justify-end pt-3 border-t border-border mt-4">
                    <GoldButton onClick={saveParams} disabled={saving} data-testid="save-api-keys"><Save className="w-3.5 h-3.5 inline mr-1"/>{saving ? "Saving…" : "Save params"}</GoldButton>
                </div>
            </div>

            <div className="bg-card border border-border">
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <div className="overline">Scope catalog ({scopes.length})</div>
                    <button onClick={() => setNewScope({ value: "", label: "", description: "" })} data-testid="add-scope"
                        className="text-[10px] uppercase tracking-[0.15em] text-primary hover:text-amber-300"><Plus className="w-3 h-3 inline"/>Add scope</button>
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-muted"><tr>{["Value","Label","Description",""].map(h => <th key={h} className="px-3 py-2 text-left text-xs uppercase tracking-wider font-medium">{h}</th>)}</tr></thead>
                    <tbody>{scopes.map(s => (
                        <tr key={s.id} className="border-t border-border" data-testid={`scope-row-${s.value}`}>
                            <td className="px-3 py-2 font-mono text-primary">{s.value}</td>
                            <td className="px-3 py-2">{s.label}</td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">{s.description}</td>
                            <td className="px-3 py-2"><button onClick={() => delScope(s)} className="text-rose-400 hover:text-rose-300" data-testid={`del-scope-${s.value}`}><Trash2 className="w-3.5 h-3.5"/></button></td>
                        </tr>
                    ))}</tbody>
                </table>
            </div>

            <CrossTenantKeysTable keys={keys} revokeKey={revokeKey}/>

            {newScope && (
                <Modal open={true} onClose={() => setNewScope(null)} title="Add scope to catalog" size="md" testId="new-scope-modal">
                    <div className="space-y-3">
                        <Field label="Value" hint="Snake_case, must contain a colon (e.g. invoices:read)">
                            <Input value={newScope.value} onChange={e => setNewScope({...newScope, value: e.target.value.toLowerCase()})} placeholder="invoices:read" data-testid="scope-value"/>
                        </Field>
                        <Field label="Label"><Input value={newScope.label} onChange={e => setNewScope({...newScope, label: e.target.value})} placeholder="Invoices — read" data-testid="scope-label"/></Field>
                        <Field label="Description"><Input value={newScope.description} onChange={e => setNewScope({...newScope, description: e.target.value})} placeholder="List & view invoice records" data-testid="scope-desc"/></Field>
                        <div className="flex justify-end gap-2 pt-3 border-t border-border">
                            <GhostButton onClick={() => setNewScope(null)}>Cancel</GhostButton>
                            <GoldButton onClick={addScope} disabled={!newScope.value || !newScope.label} data-testid="save-scope">Add</GoldButton>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

function CrossTenantKeysTable({ keys, revokeKey }) {
    const ctrl = useTableControls(keys, {
        accessors: {
            scopes: (k) => (k.scopes || []).join(" "),
            status: (k) => k.revoked_at ? "revoked" : "active",
        },
    });
    return (
        <div className="bg-card border border-border" data-testid="all-keys-table">
            <div className="px-5 py-3 border-b border-border flex items-center gap-3">
                <div className="overline">Every API key — all tenants ({keys.length})</div>
                <span className="text-[10px] text-muted-foreground">Secret stored bcrypt-hashed · revoke is permanent</span>
                <FilterChip ctrl={ctrl} className="ml-auto"/>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-muted"><tr>
                        <SortableTh field="tenant_name" ctrl={ctrl} className="px-3 py-2 text-left">Tenant</SortableTh>
                        <SortableTh field="name" ctrl={ctrl} className="px-3 py-2 text-left">Name</SortableTh>
                        <SortableTh field="display_prefix" ctrl={ctrl} className="px-3 py-2 text-left">Prefix</SortableTh>
                        <SortableTh field="scopes" ctrl={ctrl} className="px-3 py-2 text-left">Scopes</SortableTh>
                        <SortableTh field="last_used_at" ctrl={ctrl} className="px-3 py-2 text-left">Last used</SortableTh>
                        <SortableTh field="created_at" ctrl={ctrl} className="px-3 py-2 text-left">Created</SortableTh>
                        <SortableTh field="status" ctrl={ctrl} className="px-3 py-2 text-left">Status</SortableTh>
                        <th className="px-3 py-2 text-left uppercase tracking-wider font-medium"></th>
                    </tr></thead>
                    <tbody>{ctrl.rows.map(k => (
                        <tr key={k.id} className="border-t border-border" data-testid={`admin-key-row-${k.id}`}>
                            <td className="px-3 py-2"><Building2 className="w-3 h-3 inline mr-1 text-muted-foreground"/>{k.tenant_name}</td>
                            <td className="px-3 py-2 font-medium">{k.name}</td>
                            <td className="px-3 py-2 font-mono text-primary">{k.display_prefix}</td>
                            <td className="px-3 py-2"><div className="flex flex-wrap gap-1">{(k.scopes || []).map(s => <span key={s} className="px-1.5 py-0.5 bg-card border border-border font-mono text-[10px]">{s}</span>)}</div></td>
                            <td className="px-3 py-2 text-muted-foreground">{k.last_used_at ? new Date(k.last_used_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false }) : "—"}</td>
                            <td className="px-3 py-2 text-muted-foreground">{new Date(k.created_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}</td>
                            <td className="px-3 py-2">{k.revoked_at ? <span className="text-muted-foreground uppercase tracking-[0.1em] text-[10px]">Revoked</span> : <span className="text-emerald-400 uppercase tracking-[0.1em] text-[10px]">Active</span>}</td>
                            <td className="px-3 py-2">{!k.revoked_at && (
                                <button onClick={() => revokeKey(k)} className="text-rose-400 hover:text-rose-300 text-[10px] uppercase tracking-[0.1em]" data-testid={`admin-revoke-${k.id}`}>Revoke</button>
                            )}</td>
                        </tr>
                    ))}</tbody>
                </table>
            </div>
        </div>
    );
}

// ====================================================================
// WHITE-LABEL TAB
// ====================================================================
function WhiteLabelTab({ cfg, reload }) {
    const [params, setParams] = useState(cfg.white_label);
    const [overview, setOverview] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => { setParams(cfg.white_label); }, [cfg]);
    const load = useCallback(async () => {
        try { const r = await api.get("/admin/enterprise/branding-overview"); setOverview(r.data || []); }
        catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setSaving(true);
        try { await api.patch("/admin/enterprise/config", { white_label: params }); toast.success("Saved"); reload(); }
        catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const forceReset = async (t) => {
        if (!window.confirm(`Force-reset branding for "${t.tenant_name}"? Their custom logo, name, color and domain will be wiped (audit-logged).`)) return;
        try { await api.post(`/admin/enterprise/branding/${t.tenant_id}/reset`); toast.success("Branding reset"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="space-y-8">
            <ExplainerCard title="White-label — what each parameter does" rows={[
                ["max_logo_size_kb", "Upload endpoint rejects files larger than this. Returns 400 with the limit so tenant UI can show the actual cap."],
                ["allowed_logo_mimes", "Mime allow-list at the upload endpoint. Reject SVG if you have XSS concerns by removing image/svg+xml."],
                ["default_primary_color", "Used by frontend when a tenant on white_label hasn't picked a color yet."],
                ["approved_palette", "Color chips shown in the tenant's color picker. Empty list = free-form hex only."],
                ["block_custom_domain", "When ON, tenant PATCH /tenant/branding refuses any change to custom_domain field (400 error)."],
            ]}/>

            <div className="bg-card border border-border p-5">
                <div className="overline mb-4">Parameters</div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Max logo size (KB)"><Input type="number" min={32} max={10240} value={params.max_logo_size_kb} onChange={e => setParams({...params, max_logo_size_kb: Number(e.target.value)})} data-testid="max-logo"/></Field>
                    <Field label="Default primary color">
                        <div className="flex gap-2">
                            <input type="color" value={params.default_primary_color} onChange={e => setParams({...params, default_primary_color: e.target.value})} className="w-12 h-10 bg-transparent border border-border" data-testid="default-color-picker"/>
                            <Input value={params.default_primary_color} onChange={e => setParams({...params, default_primary_color: e.target.value})} className="font-mono" data-testid="default-color-input"/>
                        </div>
                    </Field>
                    <Field label="Allowed logo MIME types (CSV)">
                        <Input value={(params.allowed_logo_mimes || []).join(", ")} onChange={e => setParams({...params, allowed_logo_mimes: e.target.value.split(",").map(s=>s.trim()).filter(Boolean)})} data-testid="allowed-mimes"/>
                    </Field>
                    <Field label="Approved palette (CSV of #hex)">
                        <Input value={(params.approved_palette || []).join(", ")} onChange={e => setParams({...params, approved_palette: e.target.value.split(",").map(s=>s.trim()).filter(Boolean)})} data-testid="palette"/>
                    </Field>
                </div>
                <div className="flex items-center gap-2 pt-3">
                    <input id="block-domain" type="checkbox" checked={!!params.block_custom_domain} onChange={e => setParams({...params, block_custom_domain: e.target.checked})} data-testid="block-domain"/>
                    <label htmlFor="block-domain" className="text-xs">Block tenants from setting a custom domain</label>
                </div>
                <div className="flex justify-end pt-3 border-t border-border mt-4">
                    <GoldButton onClick={save} disabled={saving} data-testid="save-white-label"><Save className="w-3.5 h-3.5 inline mr-1"/>{saving ? "Saving…" : "Save params"}</GoldButton>
                </div>
            </div>

            <div className="bg-card border border-border" data-testid="branding-overview">
                <div className="px-5 py-3 border-b border-border overline">Every tenant's branding ({overview.length})</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-muted"><tr>{["Tenant","App name","Color","Logo","Domain","Updated",""].map(h => <th key={h} className="px-3 py-2 text-left uppercase tracking-wider font-medium">{h}</th>)}</tr></thead>
                        <tbody>{overview.map(t => (
                            <tr key={t.tenant_id} className="border-t border-border">
                                <td className="px-3 py-2"><Building2 className="w-3 h-3 inline mr-1 text-muted-foreground"/>{t.tenant_name}</td>
                                <td className="px-3 py-2">{t.app_name || <span className="text-muted-foreground">—</span>}</td>
                                <td className="px-3 py-2 font-mono"><div className="flex items-center gap-2"><div className="w-4 h-4 border border-border" style={{background:t.primary_color || "#171717"}}/><span>{t.primary_color || "—"}</span></div></td>
                                <td className="px-3 py-2">{t.has_logo ? <span className="text-emerald-400">{t.logo_size_kb?.toFixed(0)} KB</span> : <span className="text-muted-foreground">—</span>}</td>
                                <td className="px-3 py-2 font-mono">{t.custom_domain || <span className="text-muted-foreground">—</span>}</td>
                                <td className="px-3 py-2 text-muted-foreground">{t.updated_at ? new Date(t.updated_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}</td>
                                <td className="px-3 py-2">{(t.app_name || t.has_logo || t.custom_domain) && (
                                    <button onClick={() => forceReset(t)} className="text-rose-400 hover:text-rose-300 text-[10px] uppercase tracking-[0.1em]" data-testid={`force-reset-${t.tenant_id}`}>
                                        <RefreshCw className="w-3 h-3 inline"/> Reset
                                    </button>
                                )}</td>
                            </tr>
                        ))}</tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ====================================================================
// PRIORITY SUPPORT TAB
// ====================================================================
function PrioritySupportTab() {
    const [global, setGlobal] = useState(null);
    const [overrides, setOverrides] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [editOv, setEditOv] = useState(null);
    const [newHoliday, setNewHoliday] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const [g, o, h, t] = await Promise.all([
                api.get("/platform/priority-support"),
                api.get("/admin/enterprise/priority-overrides"),
                api.get("/admin/enterprise/priority-holidays"),
                api.get("/tenants").catch(() => ({ data: [] })),
            ]);
            setGlobal(g.data); setOverrides(o.data || []); setHolidays(h.data || []); setTenants(t.data || []);
        } catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (!global) return <div className="text-sm text-muted-foreground">Loading…</div>;

    const saveGlobal = async () => {
        setSaving(true);
        try { await api.patch("/platform/priority-support", global); toast.success("Global config saved"); load(); }
        catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const saveOverride = async () => {
        try { await api.post("/admin/enterprise/priority-overrides", editOv); toast.success("Override saved"); setEditOv(null); load(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const delOverride = async (o) => {
        if (!window.confirm(`Remove override for ${o.tenant_name}? Tenant falls back to platform defaults.`)) return;
        try { await api.delete(`/admin/enterprise/priority-overrides/${o.id}`); toast.success("Override removed"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const addHoliday = async () => {
        try { await api.post("/admin/enterprise/priority-holidays", newHoliday); toast.success("Holiday added"); setNewHoliday(null); load(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const delHoliday = async (h) => {
        if (!window.confirm(`Delete ${h.date} — ${h.label}?`)) return;
        try { await api.delete(`/admin/enterprise/priority-holidays/${h.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="space-y-8">
            <ExplainerCard title="Priority Support — precedence" rows={[
                ["Global config", "Default SLA shown to every Enterprise tenant. Set below — saved at platform_settings.priority_support."],
                ["Per-tenant overrides", "Highest priority. If a tenant has an override row, those fields beat the global config."],
                ["Holiday schedule", "Dates where the SLA is paused. Tenant page shows 'SLA suspended today' banner and next 8 upcoming dates."],
            ]}/>

            <div className="bg-card border border-border p-5">
                <div className="overline mb-4">Global platform config</div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="SLA (business hours)"><Input type="number" min={1} max={72} value={global.sla_hours || ""} onChange={e => setGlobal({...global, sla_hours: Number(e.target.value)})} data-testid="g-sla"/></Field>
                    <Field label="Email"><Input value={global.email || ""} onChange={e => setGlobal({...global, email: e.target.value})} data-testid="g-email"/></Field>
                    <Field label="Phone"><Input value={global.phone || ""} onChange={e => setGlobal({...global, phone: e.target.value})} data-testid="g-phone"/></Field>
                    <Field label="WhatsApp"><Input value={global.whatsapp || ""} onChange={e => setGlobal({...global, whatsapp: e.target.value})} data-testid="g-whatsapp"/></Field>
                    <Field label="KB URL"><Input value={global.kb_url || ""} onChange={e => setGlobal({...global, kb_url: e.target.value})} data-testid="g-kb"/></Field>
                </div>
                <Field label="Body (markdown, {sla_hours} is interpolated)">
                    <Textarea rows={3} value={global.body_md || ""} onChange={e => setGlobal({...global, body_md: e.target.value})} data-testid="g-body"/>
                </Field>
                <div className="flex justify-end pt-3 border-t border-border mt-4">
                    <GoldButton onClick={saveGlobal} disabled={saving} data-testid="save-global-priority"><Save className="w-3.5 h-3.5 inline mr-1"/>{saving ? "Saving…" : "Save global"}</GoldButton>
                </div>
            </div>

            <div className="bg-card border border-border" data-testid="priority-overrides">
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <div className="overline">Per-tenant overrides ({overrides.length})</div>
                    <button onClick={() => setEditOv({ tenant_id: "", sla_hours: 1, email: "", phone: "", whatsapp: "", body_md: "", enabled: true })} data-testid="new-override"
                        className="text-[10px] uppercase tracking-[0.15em] text-primary hover:text-amber-300"><Plus className="w-3 h-3 inline"/>New override</button>
                </div>
                <table className="w-full text-xs">
                    <thead className="bg-muted"><tr>{["Tenant","SLA","Email","Phone","WhatsApp","Enabled",""].map(h => <th key={h} className="px-3 py-2 text-left uppercase tracking-wider font-medium">{h}</th>)}</tr></thead>
                    <tbody>{overrides.length === 0 ? (
                        <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No overrides. All Enterprise tenants use the global config above.</td></tr>
                    ) : overrides.map(o => (
                        <tr key={o.id} className="border-t border-border">
                            <td className="px-3 py-2"><Building2 className="w-3 h-3 inline mr-1 text-muted-foreground"/>{o.tenant_name}</td>
                            <td className="px-3 py-2 font-mono">{o.sla_hours || "—"}h</td>
                            <td className="px-3 py-2">{o.email || "—"}</td>
                            <td className="px-3 py-2">{o.phone || "—"}</td>
                            <td className="px-3 py-2">{o.whatsapp || "—"}</td>
                            <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-[0.1em] ${o.enabled ? "text-emerald-400" : "text-muted-foreground"}`}>{o.enabled ? "On" : "Off"}</span></td>
                            <td className="px-3 py-2">
                                <button onClick={() => setEditOv({...o, tenant_id: o.tenant_id})} className="text-primary hover:text-amber-300 text-[10px] uppercase tracking-[0.1em] mr-2">Edit</button>
                                <button onClick={() => delOverride(o)} className="text-rose-400 hover:text-rose-300 text-[10px] uppercase tracking-[0.1em]" data-testid={`del-override-${o.id}`}>Remove</button>
                            </td>
                        </tr>
                    ))}</tbody>
                </table>
            </div>

            <div className="bg-card border border-border" data-testid="holiday-schedule">
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <div className="overline">Holiday schedule ({holidays.length})</div>
                    <button onClick={() => setNewHoliday({ date: "", label: "", sla_suspended: true })} data-testid="new-holiday"
                        className="text-[10px] uppercase tracking-[0.15em] text-primary hover:text-amber-300"><Plus className="w-3 h-3 inline"/>Add date</button>
                </div>
                <table className="w-full text-xs">
                    <thead className="bg-muted"><tr>{["Date","Label","SLA suspended",""].map(h => <th key={h} className="px-3 py-2 text-left uppercase tracking-wider font-medium">{h}</th>)}</tr></thead>
                    <tbody>{holidays.length === 0 ? (
                        <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">No holidays scheduled.</td></tr>
                    ) : holidays.map(h => (
                        <tr key={h.id} className="border-t border-border">
                            <td className="px-3 py-2 font-mono">{h.date}</td>
                            <td className="px-3 py-2">{h.label}</td>
                            <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-[0.1em] ${h.sla_suspended ? "text-amber-300" : "text-muted-foreground"}`}>{h.sla_suspended ? "Yes" : "No"}</span></td>
                            <td className="px-3 py-2"><button onClick={() => delHoliday(h)} className="text-rose-400 hover:text-rose-300" data-testid={`del-holiday-${h.id}`}><Trash2 className="w-3.5 h-3.5"/></button></td>
                        </tr>
                    ))}</tbody>
                </table>
            </div>

            {editOv && (
                <Modal open={true} onClose={() => setEditOv(null)} title={editOv.id ? "Edit override" : "New tenant override"} size="lg" testId="override-modal">
                    <div className="space-y-4">
                        <Field label="Tenant">
                            <Select options={[{value:"",label:"— pick tenant —"}, ...tenants.map(t => ({value: t.id, label: t.name}))]}
                                value={editOv.tenant_id} onChange={e => setEditOv({...editOv, tenant_id: e.target.value})} name="t"/>
                        </Field>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="SLA hours"><Input type="number" min={1} max={72} value={editOv.sla_hours || ""} onChange={e => setEditOv({...editOv, sla_hours: Number(e.target.value)})} data-testid="o-sla"/></Field>
                            <Field label="Email"><Input value={editOv.email || ""} onChange={e => setEditOv({...editOv, email: e.target.value})} data-testid="o-email"/></Field>
                            <Field label="Phone"><Input value={editOv.phone || ""} onChange={e => setEditOv({...editOv, phone: e.target.value})} data-testid="o-phone"/></Field>
                            <Field label="WhatsApp"><Input value={editOv.whatsapp || ""} onChange={e => setEditOv({...editOv, whatsapp: e.target.value})} data-testid="o-whatsapp"/></Field>
                        </div>
                        <Field label="Body (override)"><Textarea rows={3} value={editOv.body_md || ""} onChange={e => setEditOv({...editOv, body_md: e.target.value})} data-testid="o-body"/></Field>
                        <div className="flex items-center gap-2"><input id="ov-enabled" type="checkbox" checked={!!editOv.enabled} onChange={e => setEditOv({...editOv, enabled: e.target.checked})}/><label htmlFor="ov-enabled" className="text-xs">Enabled</label></div>
                        <div className="flex justify-end gap-2 pt-3 border-t border-border">
                            <GhostButton onClick={() => setEditOv(null)}>Cancel</GhostButton>
                            <GoldButton onClick={saveOverride} disabled={!editOv.tenant_id} data-testid="save-override">Save</GoldButton>
                        </div>
                    </div>
                </Modal>
            )}

            {newHoliday && (
                <Modal open={true} onClose={() => setNewHoliday(null)} title="Add holiday" size="md" testId="holiday-modal">
                    <div className="space-y-3">
                        <Field label="Date (YYYY-MM-DD)"><Input type="date" value={newHoliday.date} onChange={e => setNewHoliday({...newHoliday, date: e.target.value})} data-testid="h-date"/></Field>
                        <Field label="Label"><Input value={newHoliday.label} onChange={e => setNewHoliday({...newHoliday, label: e.target.value})} placeholder="Diwali / Holi / Republic Day" data-testid="h-label"/></Field>
                        <div className="flex items-center gap-2"><input id="h-sus" type="checkbox" checked={newHoliday.sla_suspended} onChange={e => setNewHoliday({...newHoliday, sla_suspended: e.target.checked})}/><label htmlFor="h-sus" className="text-xs">SLA suspended on this date</label></div>
                        <div className="flex justify-end gap-2 pt-3 border-t border-border">
                            <GhostButton onClick={() => setNewHoliday(null)}>Cancel</GhostButton>
                            <GoldButton onClick={addHoliday} disabled={!newHoliday.date || !newHoliday.label} data-testid="save-holiday">Add</GoldButton>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// ====================================================================
// FIELD MASKS TAB
// ====================================================================
function FieldMasksTab({ cfg, reload }) {
    const [params, setParams] = useState(cfg.field_masks);
    const [templates, setTemplates] = useState([]);
    const [overview, setOverview] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [applying, setApplying] = useState(null);
    const [editingTpl, setEditingTpl] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => { setParams(cfg.field_masks); }, [cfg]);
    const load = useCallback(async () => {
        try {
            const [t, o, ts] = await Promise.all([
                api.get("/admin/enterprise/field-mask-templates"),
                api.get("/admin/enterprise/field-masks-overview"),
                api.get("/tenants").catch(() => ({ data: [] })),
            ]);
            setTemplates(t.data || []); setOverview(o.data || []); setTenants(ts.data || []);
        } catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const saveParams = async () => {
        setSaving(true);
        try { await api.patch("/admin/enterprise/config", { field_masks: params }); toast.success("Saved"); reload(); }
        catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };
    const apply = async () => {
        try { const r = await api.post("/admin/enterprise/field-masks/apply-template", applying); toast.success(`Applied — ${r.data.rules_added} rules added`); setApplying(null); load(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const deleteTpl = async (t) => {
        if (!window.confirm(`Delete template "${t.name}"? Any mandatory rules pushed from it across tenants will also be removed.`)) return;
        try { await api.delete(`/admin/enterprise/field-mask-templates/${t.id}`); toast.success("Template deleted"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const saveTpl = async () => {
        try { await api.post("/admin/enterprise/field-mask-templates", editingTpl); toast.success("Template created"); setEditingTpl(null); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="space-y-8">
            <ExplainerCard title="Field Masks — what each parameter does" rows={[
                ["max_rules_per_tenant", "Tenant POST /tenant/field-masks returns 400 once they hit this count."],
                ["Templates", "Reusable rule bundles. Apply with one click to N tenants. If template.mandatory=true, the inserted rules are read-only for tenants (they can't disable or delete)."],
                ["Mandatory rules", "Pushed by you. Bypass plan-flag check — they STILL apply even if the tenant's advanced_rbac flag is turned off (use for DPDP/GDPR baseline enforcement)."],
            ]}/>

            <div className="bg-card border border-border p-5">
                <div className="overline mb-4">Parameters</div>
                <Field label="Max rules per tenant"><Input type="number" min={1} max={500} value={params.max_rules_per_tenant} onChange={e => setParams({...params, max_rules_per_tenant: Number(e.target.value)})} data-testid="max-rules"/></Field>
                <div className="flex justify-end pt-3 border-t border-border mt-4">
                    <GoldButton onClick={saveParams} disabled={saving} data-testid="save-field-masks"><Save className="w-3.5 h-3.5 inline mr-1"/>{saving ? "Saving…" : "Save"}</GoldButton>
                </div>
            </div>

            <div className="bg-card border border-border" data-testid="templates-list">
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <div className="overline">Templates ({templates.length})</div>
                    <button onClick={() => setEditingTpl({ name: "", description: "", mandatory: false, rules: [] })} data-testid="new-template"
                        className="text-[10px] uppercase tracking-[0.15em] text-primary hover:text-amber-300"><Plus className="w-3 h-3 inline"/>New template</button>
                </div>
                <div className="divide-y divide-border">{templates.map(t => (
                    <div key={t.id} className="px-5 py-3 flex items-start justify-between gap-3" data-testid={`tpl-${t.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="font-medium">{t.name}</span>
                                {t.mandatory && <span className="text-[10px] px-2 py-0.5 bg-amber-950/40 text-amber-300 uppercase tracking-[0.1em]">Mandatory</span>}
                                <span className="text-[10px] text-muted-foreground">{t.rules.length} rules</span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">{t.description}</div>
                            <div className="mt-2 flex flex-wrap gap-1">{t.rules.slice(0, 5).map((r, i) => (
                                <span key={i} className="text-[10px] font-mono px-2 py-0.5 bg-card border border-border">{r.collection}.{r.field} → {r.mode}</span>
                            ))}{t.rules.length > 5 && <span className="text-[10px] text-muted-foreground">+{t.rules.length-5} more</span>}</div>
                        </div>
                        <div className="flex gap-3 shrink-0">
                            <button onClick={() => setApplying({ template_id: t.id, tenant_id: "", _name: t.name })}
                                className="text-[10px] uppercase tracking-[0.1em] text-primary hover:text-amber-300" data-testid={`apply-tpl-${t.id}`}>Apply →</button>
                            <button onClick={() => deleteTpl(t)} className="text-rose-400 hover:text-rose-300" data-testid={`del-tpl-${t.id}`}><Trash2 className="w-3.5 h-3.5"/></button>
                        </div>
                    </div>
                ))}</div>
            </div>

            <div className="bg-card border border-border" data-testid="masks-overview">
                <div className="px-5 py-3 border-b border-border overline">All rules across all tenants ({overview.length})</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-muted"><tr>{["Tenant","Collection.field","Roles","Mode","Mandatory","Enabled","Source"].map(h => <th key={h} className="px-3 py-2 text-left uppercase tracking-wider font-medium">{h}</th>)}</tr></thead>
                        <tbody>{overview.map(r => (
                            <tr key={r.id} className="border-t border-border">
                                <td className="px-3 py-2"><Building2 className="w-3 h-3 inline mr-1 text-muted-foreground"/>{r.tenant_name}</td>
                                <td className="px-3 py-2 font-mono">{r.collection}.<span className="text-primary">{r.field}</span></td>
                                <td className="px-3 py-2"><div className="flex flex-wrap gap-1">{(r.roles || []).map(role => <span key={role} className="text-[10px] font-mono px-1.5 py-0.5 bg-card border border-border">{role}</span>)}</div></td>
                                <td className="px-3 py-2"><span className={r.mode === "hide" ? "text-rose-400" : "text-amber-300"}>{r.mode}</span></td>
                                <td className="px-3 py-2">{r.mandatory ? <span className="text-amber-300 text-[10px] uppercase">Yes</span> : <span className="text-muted-foreground text-[10px]">No</span>}</td>
                                <td className="px-3 py-2"><span className={r.enabled ? "text-emerald-400" : "text-muted-foreground"}>{r.enabled ? "On" : "Off"}</span></td>
                                <td className="px-3 py-2 text-[10px] text-muted-foreground">{r.applied_from_template ? "template" : "tenant"}</td>
                            </tr>
                        ))}</tbody>
                    </table>
                </div>
            </div>

            {applying && (
                <Modal open={true} onClose={() => setApplying(null)} title={`Apply template — ${applying._name}`} size="md" testId="apply-modal">
                    <div className="space-y-3">
                        <div className="border border-amber-700/40 bg-amber-950/20 p-3 flex items-start gap-2 text-xs">
                            <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0"/>
                            <div className="text-amber-100">Rules will be inserted onto the chosen tenant. If the template is <strong>mandatory</strong>, those rules become read-only for the tenant — they can't disable or delete them.</div>
                        </div>
                        <Field label="Apply to tenant">
                            <Select options={[{value:"",label:"— pick tenant —"}, ...tenants.map(t => ({value: t.id, label: t.name}))]}
                                value={applying.tenant_id} onChange={e => setApplying({...applying, tenant_id: e.target.value})} name="t"/>
                        </Field>
                        <div className="flex justify-end gap-2 pt-3 border-t border-border">
                            <GhostButton onClick={() => setApplying(null)}>Cancel</GhostButton>
                            <GoldButton onClick={apply} disabled={!applying.tenant_id} data-testid="confirm-apply">Apply</GoldButton>
                        </div>
                    </div>
                </Modal>
            )}

            {editingTpl && <TemplateBuilder draft={editingTpl} setDraft={setEditingTpl} onSave={saveTpl}/>}
        </div>
    );
}

function TemplateBuilder({ draft, setDraft, onSave }) {
    const COLS = ["clients", "cases", "invoices", "hearings", "documents"];
    const ROLES = ["managing_partner","advocate","senior_associate","junior_associate","paralegal","clerk","accountant"];
    const addRule = () => setDraft({...draft, rules: [...draft.rules, { collection: "clients", field: "", roles: [], mode: "mask" }]});
    const updRule = (i, patch) => setDraft({...draft, rules: draft.rules.map((r, idx) => idx === i ? {...r, ...patch} : r)});
    const delRule = (i) => setDraft({...draft, rules: draft.rules.filter((_, idx) => idx !== i)});
    return (
        <Modal open={true} onClose={() => setDraft(null)} title="New template" size="xl" testId="tpl-modal">
            <div className="space-y-4">
                <Field label="Name"><Input value={draft.name} onChange={e => setDraft({...draft, name: e.target.value})} data-testid="tpl-name"/></Field>
                <Field label="Description"><Input value={draft.description} onChange={e => setDraft({...draft, description: e.target.value})} data-testid="tpl-desc"/></Field>
                <div className="flex items-center gap-2"><input id="mand" type="checkbox" checked={draft.mandatory} onChange={e => setDraft({...draft, mandatory: e.target.checked})} data-testid="tpl-mandatory"/><label htmlFor="mand" className="text-xs">Mandatory — tenants can't disable rules from this template (compliance enforcement)</label></div>
                <div className="border-t border-border pt-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="overline">Rules ({draft.rules.length})</div>
                        <button onClick={addRule} className="text-[10px] uppercase tracking-[0.15em] text-primary hover:text-amber-300" data-testid="tpl-add-rule"><Plus className="w-3 h-3 inline"/>Add rule</button>
                    </div>
                    <div className="space-y-2 max-h-72 overflow-y-auto">{draft.rules.map((r, i) => (
                        <div key={i} className="border border-border p-2 grid grid-cols-12 gap-2 items-center text-xs" data-testid={`tpl-rule-${i}`}>
                            <select value={r.collection} onChange={e => updRule(i, { collection: e.target.value })} className="col-span-3 bg-card border border-border px-2 py-1">{COLS.map(c => <option key={c} value={c}>{c}</option>)}</select>
                            <input value={r.field} onChange={e => updRule(i, { field: e.target.value })} placeholder="field e.g. phone" className="col-span-3 bg-card border border-border px-2 py-1 font-mono"/>
                            <input value={(r.roles || []).join(",")} onChange={e => updRule(i, { roles: e.target.value.split(",").map(s=>s.trim()).filter(Boolean) })} placeholder="roles CSV" className="col-span-4 bg-card border border-border px-2 py-1 font-mono text-[10px]"/>
                            <select value={r.mode} onChange={e => updRule(i, { mode: e.target.value })} className="col-span-1 bg-card border border-border px-2 py-1"><option value="mask">mask</option><option value="hide">hide</option></select>
                            <button onClick={() => delRule(i)} className="col-span-1 text-rose-400 hover:text-rose-300 justify-self-end"><Trash2 className="w-3 h-3"/></button>
                        </div>
                    ))}</div>
                    <div className="text-[10px] text-muted-foreground mt-2">Available roles: {ROLES.join(", ")}.</div>
                </div>
                <div className="flex justify-end gap-2 pt-3 border-t border-border">
                    <GhostButton onClick={() => setDraft(null)}>Cancel</GhostButton>
                    <GoldButton onClick={onSave} disabled={!draft.name || draft.rules.length === 0} data-testid="save-tpl">Create template</GoldButton>
                </div>
            </div>
        </Modal>
    );
}

// ====================================================================
// Shared
// ====================================================================
function ExplainerCard({ title, rows }) {
    return (
        <div className="border border-primary/30 bg-primary/5 p-5" data-testid="explainer">
            <div className="flex items-center gap-2 mb-3"><BookOpen className="w-4 h-4 text-primary"/><div className="text-sm font-medium">{title}</div></div>
            <table className="w-full text-xs">
                <tbody>{rows.map(([k, v]) => (
                    <tr key={k} className="align-top">
                        <td className="py-1 pr-4 font-mono text-primary whitespace-nowrap">{k}</td>
                        <td className="py-1 text-muted-foreground">{v}</td>
                    </tr>
                ))}</tbody>
            </table>
        </div>
    );
}
